import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, auth, usingOrbitIdentity, type Me, type SpaceOption } from "@/api/client";
import { AphelionLogo } from "@/components/AphelionLogo";
import { EMPTY_OTP, OtpInput } from "@/components/OtpInput";
import { ThemeToggle } from "@/components/ThemeToggle";
import { MfaEnrolPage } from "@/auth/MfaEnrolPage";
import { useSession } from "@/auth/session";

/** Every error code Fabric's uds-discover / uds-enter can return
 *  (canonical list confirmed 2026-09-22; mirrored in
 *  shared/lib/fabric_auth/exceptions.py::LOGIN_ERROR_CODES).
 *
 *  Showing the real status is DELIBERATE and safe: Fabric verifies the
 *  PASSWORD FIRST, so every message below is only ever reached by someone who
 *  already proved the password. The single exception is ACCOUNT_LOCKED, which
 *  is checked before the password on purpose — that is the point of a lockout,
 *  to stop burning verification attempts — and is self-inflicted only under
 *  attack. */
const ERRORS: Record<string, string> = {
  INVALID_CREDENTIALS: "Incorrect email or password",
  // FALLBACK ONLY. Fabric sends a live countdown in `detail.message` and the
  // server forwards it for this code; see the ACCOUNT_LOCKED branch below.
  // This text is what the person sees if that message is ever missing, so
  // it deliberately gives no specific number it could be wrong about.
  ACCOUNT_LOCKED: "Too many failed attempts — this account is temporarily locked",
  USER_INACTIVE: "This account is inactive",
  EMAIL_NOT_VERIFIED: "Verify your email before signing in",
  COMPANY_INACTIVE: "This company is inactive — contact your administrator",
  MFA_ENROLMENT_REQUIRED: "Set up two-factor authentication before signing in",
  MFA_INVALID: "That code didn't work — try the next one",
  NO_SPACE_ACCESS: "You have no access to Orbit — ask your administrator",
  UNKNOWN_TOOL: "Orbit is not enabled for this account",
  // Fabric's current name for the same refusal (their 2026-09-22
  // catalogue no longer contains UNKNOWN_TOOL). Same sentence on purpose.
  NO_PRODUCT_ACCESS: "Orbit is not enabled for this account",
  INVALID_SPACE: "That company or space is no longer available — sign in again",
  ROLE_NOT_ALLOWED: "This account cannot sign in to Orbit",
  RATE_LIMITED: "Too many attempts — wait a few minutes",
  MISSING_FIELDS: "Enter your email and password",
  VALIDATION: "Enter your email and password",
  FABRIC_UNREACHABLE: "Cannot reach the authentication server",
};

export function LoginPage() {
  const { setMe } = useSession();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [mfaNeeded, setMfaNeeded] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [otp, setOtp] = useState<string[]>(EMPTY_OTP);
  const [spaces, setSpaces] = useState<SpaceOption[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(code?: string, space?: SpaceOption) {
    setBusy(true);
    setError("");
    try {
      const res = await auth.login({
        login_id: loginId,
        password,
        mfa_code: code,
        company_id: space?.company_id,
        space_id: space?.space_id,
      });
      if ("spaces" in res) {
        setSpaces(res.spaces); // Fabric offered several — pick one
        return;
      }
      setMe(res as Me);
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "MFA_REQUIRED") {
        setMfaNeeded(true);
        setError("Enter the 6-digit code from your authenticator");
      } else if (err.code === "MFA_ENROLMENT_REQUIRED") {
        // The password was right and the account MAY NOT proceed without an
        // authenticator. This used to render ERRORS[...] and stop — a correct
        // sentence with no way to act on it, on an account that could not sign
        // in at all until someone did.
        setEnrolling(true);
      } else {
        // Never print a raw code at the user. An unmapped one means Fabric
        // added something we have not learned yet — log it for us, show the
        // person a sentence. The old fallback rendered e.g.
        // "Sign-in failed (COMPANY_INACTIVE)" on screen.
        if (!ERRORS[err.code]) console.warn("unmapped Fabric error code:", err.code);
        // ACCOUNT_LOCKED is the one code whose text Fabric owns — it carries a
        // real countdown that only they can compute, and a completed password
        // reset clears the lock early. Prefer it, fall back to our copy.
        // `err.message` defaults to the CODE when the server sent no message,
        // so check it is not the code before showing it — otherwise a missing
        // message puts "ACCOUNT_LOCKED" on screen.
        const live =
          err.code === "ACCOUNT_LOCKED" && err.message && err.message !== err.code
            ? err.message
            : undefined;
        setError(live ?? ERRORS[err.code] ?? "Sign-in failed — please try again");
        setOtp(EMPTY_OTP);
      }
    } finally {
      setBusy(false);
    }
  }

  if (enrolling) {
    return (
      <MfaEnrolPage
        loginId={loginId}
        onEnrolled={() => {
          // STAGE A: enrolment and session-granting are one server step
          // (`finish-enrol`), and there is no server yet — so we hand the
          // person back to sign-in with the code box already open rather than
          // fake a session we cannot create. Phase 1 replaces this callback
          // with the granted session, which is what Fabric does today.
          setEnrolling(false);
          setMfaNeeded(true);
          setPassword("");
          setError("Two-factor is set up. Sign in with your password and a code.");
        }}
      />
    );
  }

  return (
    <div className="login-wrap">
      <div style={{ position: "fixed", top: 14, right: 14, zIndex: 20 }}>
        <ThemeToggle />
      </div>
      <div className="login-card">
        <div className="login-branding">
          <AphelionLogo width={210} height={136} animated />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <span className="brand-name">Aphelion Energy</span>
            <span className="brand-tagline">Future of Energy; Engineered!</span>
            <span className="brand-version">Orbit v0.1.0</span>
          </div>
        </div>
        <div className="login-div" />
        <div className="login-form-side">
          <div className="l-eyebrow">Orbit Platform</div>
          <h2 className="l-title">Sign In</h2>
          <p className="l-sub">Enter your credentials to access your fleet</p>

          {spaces ? (
            <div>
              <div className="simlab" style={{ marginBottom: 8 }}>
                Choose a company / space
              </div>
              {spaces.map((s) => (
                <button
                  key={`${s.company_id}:${s.space_id}`}
                  className="btn ghost"
                  style={{ width: "100%", marginBottom: 8, textAlign: "left" }}
                  // disabled={busy} is load-bearing, not cosmetic. Fabric burns
                  // a TOTP timestep on first use (replay guard, 2026-09-22), so
                  // a double-click sends the SAME code twice: the first call
                  // signs the person in, the second comes back MFA_INVALID,
                  // wipes the boxes, tells them the code was wrong — and counts
                  // against the per-IP throttle. Never resend a used code.
                  disabled={busy}
                  onClick={() => void submit(otp.join("") || undefined, s)}
                >
                  {s.company_name} · {s.space_name}
                </button>
              ))}
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit(otp.every((d) => d) ? otp.join("") : undefined);
              }}
            >
              <div className="igroup">
                <label htmlFor="f-e">Email</label>
                <input
                  id="f-e"
                  className="inp"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  autoComplete="username"
                />
              </div>
              <div className="igroup">
                <label htmlFor="f-p">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    id="f-p"
                    className="inp"
                    type={showPw ? "text" : "password"}
                    style={{ paddingRight: 44 }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="pw-eye"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    onClick={() => setShowPw((v) => !v)}
                  >
                    👁
                  </button>
                </div>
              </div>

              {mfaNeeded && (
                <div className="igroup">
                  <label>MFA code (authenticator)</label>
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    onComplete={(code) => void submit(code)}
                    autoFocus
                  />
                </div>
              )}

              {error && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--bad)",
                    marginBottom: 12,
                    textAlign: "center",
                  }}
                >
                  {error}
                </div>
              )}

              <button className="btn-signin" type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Sign In →"}
              </button>

              <div style={{ textAlign: "center", marginTop: 12, fontSize: 12 }}>
                <Link to="/forgot-password">Forgot your password?</Link>
              </div>
            </form>
          )}

          {/* WHICH BACKEND, stated plainly, and it FOLLOWS the backend rather
              than being edited. Two identity systems answer this same form
              during the migration, and "Incorrect email or password" means
              something completely different depending on which one replied —
              a Fabric account is unknown to Orbit and vice versa, so a tester
              who cannot see which they hit is debugging the wrong system.

              Because it reads `usingOrbitIdentity()` rather than a constant, the
              day the console switches over it says "Secured by Orbit" on its
              own. A hardcoded string is how a footer ends up crediting a system
              that was retired months earlier. */}
          <div className="lsep">
            <span>{usingOrbitIdentity() ? "Secured by Orbit" : "Secured by Fabric"}</span>
          </div>
          {usingOrbitIdentity() && (
            <div className="cap" style={{ textAlign: "center", marginBottom: 8 }}>
              Signing in against <strong>Orbit&rsquo;s own users</strong>, not Fabric.
              Only test accounts exist here.{" "}
              <a href="?v2=0">Back to Fabric</a>
            </div>
          )}
          <div className="srvrow">
            <span className="gdot" /> orbit.aphelionenergy.com
          </div>
        </div>
      </div>
    </div>
  );
}
